import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import fragments from './fragments';
import update from 'immutability-helper';


function getQueryNameFromQuery(queryDoc) {
    let errorMessage = 'getQueryName must receive a GraphQL document with a single named GraphQL query';
    if (queryDoc.kind !== "Document") {
        throw new Error(errorMessage);
    }
    if (!Array.isArray(queryDoc.definitions)) {
        throw new Error(errorMessage);
    }
    let operationDefinitions = queryDoc.definitions.filter(definition => {
        return definition.kind === "OperationDefinition";
    });
    if (operationDefinitions.length !== 1) {
        throw new Error(errorMessage);
    }
    let definition = operationDefinitions[0];
    if (definition.operation !== "query") {
        throw new Error(errorMessage);
    }
    if (!definition.name) {
        throw new Error(errorMessage);
    }
    return definition.name.value;
}

const userListQuery = gql`
query userListQuery($projectId: ID!) {
    userList(projectId: $projectId) {
        ...userFields
    }
}
${fragments.userFields}`;

const userListQueryHoc = graphql(userListQuery, {
    name: 'userListData',
    options: (props) => ({
        variables: {
            projectId: props.projectId
        }
    })
});

const aclListQuery = gql`
query aclList($objectId: ID!) {
    aclList(objectId: $objectId) {
        ...aclFields
    }
}
${fragments.aclFields}`;

const aclListQueryHoc = graphql(aclListQuery, {
    skip: (props) => (props.project.aclRole !== "admin"),
    options: (props) => ({
        variables: { objectId: props.project.id }
    }),
});

const projectQuery = gql`
query project($projectId: ID!) {
  project(id: $projectId) {
    ...projectFields
  }
}
${fragments.projectFields}`;

const projectQueryHocOptions = (props) => ({ variables: { projectId: props.projectId || (props.params && props.params.projectId) || null } });
const projectQueryHoc = graphql(projectQuery, { options: projectQueryHocOptions });
const projectQueryHocWithName = (name) => graphql(projectQuery, { name, options: projectQueryHocOptions });

const projectsQuery = gql`
query projects {
  projects {
    ...ProjectInProjectList
  }
}
${fragments.projectFieldsForList}`;

const projectsQueryHoc = graphql(projectsQuery);
const projectsQueryHocWithName = name => graphql(projectsQuery, {name});

const boardQuery = gql`
query boardQuery($boardId: ID!) {
  board(id: $boardId) {
    id
    name
    aclRole
    queues {
      ...QueueFragment
    }
  }
}
${fragments.queueFragment}`;

const boardQueryHocOptions = (props) => ({ variables: { boardId: props.params.boardId } });
const boardQueryHoc = graphql(boardQuery, { options: boardQueryHocOptions });
const boardQueryHocWithName = (name) => graphql(boardQuery, { name, options: boardQueryHocOptions });

const queuesQuery = gql`
query queuesQuery($projectId: ID!) {
    queues(projectId: $projectId) {
        ...QueueFragment
    }
}
${fragments.queueFragment}`;

const queuesQueryHocOptions = (props) => ({ variables: { projectId: props.projectId || (props.params && props.params.projectId) || null } });
const queuesQueryHoc = graphql(queuesQuery, { options: queuesQueryHocOptions });
const queuesQueryHocWithName = (name) => graphql(queuesQuery, { name, options: queuesQueryHocOptions });

const itemQuery = gql`
query itemQuery($itemId: ID!) {
    item(itemId: $itemId) {
        ...ItemFragment
    }
}
${fragments.itemFragment}`;

const itemQueryHocOptions = (props) => ({ variables: { itemId: props.itemId } });
const itemQueryHoc = graphql(itemQuery, {options: itemQueryHocOptions});
const itemQueryHocWithName = (name) => graphql(itemQuery, { name, options: itemQueryHocOptions });

const itemListQuery = gql`
query itemListQuery($queueId: ID!, $cursor: String) {
    itemList(queueId: $queueId, cursor: $cursor) {
        ...QueueItemListFragment
    }
}
${fragments.queueItemListFragment}`;

const itemListQueryHoc = graphql(itemListQuery, {
    options: (props) => ({
        variables: {
            queueId: props.queue.id
        }
    }),
    props: ({data, ownProps}) => ({
        items: data,
        loadMoreItems: () => {
            return data.fetchMore({
                variables: {
                    queueId: ownProps.queue.id,
                    cursor: data.itemList.nextCursor
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    if (!fetchMoreResult) { return previousResult; }
                    return update(previousResult, {
                        itemList: {
                            items: {$push: fetchMoreResult.itemList.items},
                            hasMore: {$set: fetchMoreResult.itemList.hasMore},
                            nextCursor: {$set: fetchMoreResult.itemList.nextCursor}
                        }
                    });
                }
            });
        }
    })
});

const itemLogsQuery = gql`
query itemLogsQuery($itemId: ID!) {
    item(id: $itemId) {
        id
        logs {
            id
            createdAt
            message
        }
    }
}`;

const itemLogsQueryHoc = graphql(itemLogsQuery, {
    name: 'logsData',
    options: (props) => ({ variables: { itemId: props.itemId } })
});

const itemDataFieldsQuery = gql`
query itemDataFields($projectId: ID!) {
    itemDataFields(projectId: $projectId) {
        key
        types
    }
}`;

const itemDataFieldsQueryHoc = graphql(itemDataFieldsQuery, {
    name: 'itemDataFieldsData',
    options: (props) => ({ variables: { projectId: props.projectId } })
});

const searchQuery = gql`
query searchQuery($projectId: ID!, $searchText: String!) {
    search(projectId: $projectId, searchText: $searchText) {
        ...ItemFragment
    }
}
${fragments.itemFragment}`;

const searchQueryHoc = graphql(searchQuery, {
    skip: (props) => !props.searchText || props.searchText.length < 3,
    options: (props) => ({
        variables: {
            projectId: props.projectId,
            searchText: props.searchText
        }
    })
});

const itemCountMatrixByStatesAndDays = gql`
query itemCountMatrixByStatesAndDaysQuery($projectId: ID!) {
    itemCountMatrixByStatesAndDays(projectId: $projectId)
}`;

const itemCountMatrixQueryHocOptions = (props) => ({ variables: { projectId: props.project.id } });
const itemCountMatrixByStatesAndDaysHoc = graphql(itemCountMatrixByStatesAndDays, { options: itemCountMatrixQueryHocOptions });
const itemCountMatrixByStatesAndDaysHocWithName = (name) => graphql(itemCountMatrixByStatesAndDays, { name, options: itemCountMatrixQueryHocOptions });


const itemCountByStatesQuery = gql`
query itemCountByStatesQuery($projectId: ID!) {
    itemCountByStates(projectId: $projectId)
}`;

const itemCountByStatesQueryHocOptions = (props) => ({ variables: { projectId: props.project.id } });
const itemCountByStatesQueryHoc = graphql(itemCountByStatesQuery, { options: itemCountByStatesQueryHocOptions });
const itemCountByStatesQueryHocWithName = (name) => graphql(itemCountByStatesQuery, { name, options: itemCountByStatesQueryHocOptions });


const stateEntrancesAndExitsCountByHoursQuery = gql`
query stateEntrancesAndExitsCountByHoursQuery($projectId: ID!) {
    stateEntrancesAndExitsCountByHours(projectId: $projectId)
}`;

const stateEntrancesAndExitsCountByHoursQueryHocOptions = (props) => ({ variables: { projectId: props.project.id } });
const stateEntrancesAndExitsCountByHoursQueryHoc = graphql(stateEntrancesAndExitsCountByHoursQuery, { options: stateEntrancesAndExitsCountByHoursQueryHocOptions });
const stateEntrancesAndExitsCountByHoursQueryHocWithName = (name) => graphql(stateEntrancesAndExitsCountByHoursQuery, { name, options: stateEntrancesAndExitsCountByHoursQueryHocOptions });

export default {
    getQueryNameFromQuery,
    userListQuery,
    userListQueryHoc,
    aclListQuery,
    aclListQueryHoc,
    projectQuery,
    projectQueryHoc,
    projectQueryHocWithName,
    projectsQuery,
    projectsQueryHoc,
    projectsQueryHocWithName,
    boardQuery,
    boardQueryHoc,
    boardQueryHocWithName,
    queuesQuery,
    queuesQueryHoc,
    queuesQueryHocWithName,
    itemQuery,
    itemQueryHoc,
    itemQueryHocWithName,
    itemListQuery,
    itemListQueryHoc,
    itemLogsQuery,
    itemLogsQueryHoc,
    itemDataFieldsQuery,
    itemDataFieldsQueryHoc,
    searchQuery,
    searchQueryHoc,
    itemCountMatrixByStatesAndDays,
    itemCountMatrixByStatesAndDaysHoc,
    itemCountMatrixByStatesAndDaysHocWithName,
    stateEntrancesAndExitsCountByHoursQuery,
    stateEntrancesAndExitsCountByHoursQueryHoc,
    stateEntrancesAndExitsCountByHoursQueryHocWithName,
    itemCountByStatesQuery,
    itemCountByStatesQueryHoc,
    itemCountByStatesQueryHocWithName
};



// WEBPACK FOOTER //
// ./src/graphql/queries.js